// Workforce Readiness module content for Care Comply.

export const WORKFORCE_INTRO =
  "Many CDA candidates complete their 120 hours of training but have never worked in a professional childcare environment. This module builds the practical employment skills you need to succeed in interviews, on the job, and across a long career in early childhood education.";

export const WORKFORCE_MODULES = [
  {
    id: 1,
    title: "Professional Appearance",
    tagline: "First impressions matter.",
    intro: "Dress for success when interviewing or working in a childcare center.",
    recommended: [
      "Clean, professional clothing",
      "Comfortable closed-toe shoes (tennis or work shoes preferred)",
      "Medical scrubs are popular — comfortable, professional, and handy pockets",
      "Clothing that allows movement, floor play, lifting, and active engagement",
    ],
    avoid: [
      "Stilettos or high heels",
      "Excessive jewelry",
      "Offensive language or graphics",
      "Revealing clothing",
      "Unsafe footwear",
    ],
  },
  {
    id: 2,
    title: "Personal Hygiene & Grooming",
    tagline: "Professionalism begins with self-care.",
    intro: "Children, families, and employers notice professionalism.",
    recommended: [
      "Arrive showered and clean",
      "Wear clean clothing",
      "Practice good hygiene and use deodorant",
      "Keep hair neat and manageable",
      "Maintain clean fingernails",
    ],
    avoid: [],
  },
  {
    id: 3,
    title: "Attendance & Reliability",
    tagline: "Dependability is one of the most valued skills in childcare.",
    intro: "Reliability builds trust with employers.",
    recommended: [
      "Arrive on time",
      "Plan transportation in advance",
      "Be prepared before your shift begins",
      "Follow the center's attendance policies",
      "If running late: call immediately, notify your supervisor, give an ETA",
      "If you can't come in: call as soon as possible — never 'no call, no show'",
    ],
    avoid: [],
  },
  {
    id: 4,
    title: "Professional Communication",
    tagline: "How you communicate matters.",
    intro: "Be respectful to families, children, and coworkers.",
    recommended: [
      "Speak respectfully and listen actively",
      "Ask questions when unsure",
      "Accept feedback professionally",
      "Use appropriate language and positive body language",
    ],
    avoid: [
      "Gossip and workplace drama",
      "Negative social media discussions about work",
      "Arguing with coworkers in front of children",
      "Unprofessional texting during work hours",
    ],
  },
  {
    id: 5,
    title: "Professional Boundaries",
    tagline: "Keep your personal life separate from your responsibilities.",
    intro: "You are a professional educator and role model.",
    recommended: [
      "Maintain confidentiality",
      "Respect family privacy",
      "Avoid oversharing personal information",
      "Keep workplace conversations professional",
      "Protect children's information",
    ],
    avoid: [],
  },
  {
    id: 6,
    title: "Customer Service & Family Engagement",
    tagline: "Families are trusting you with their most valuable asset — their child.",
    intro: "Every interaction represents your center and your profession.",
    recommended: [
      "Smile and greet families warmly",
      "Be welcoming",
      "Show patience and kindness",
      "Create a positive environment",
    ],
    avoid: [],
  },
  {
    id: 7,
    title: "Teamwork in Early Childhood Education",
    tagline: "Childcare is a team profession.",
    intro: "Effective team members make great classrooms.",
    recommended: [
      "Help coworkers when needed",
      "Communicate respectfully",
      "Share classroom responsibilities and support routines",
      "Be flexible",
      "Participate in staff meetings and trainings",
    ],
    avoid: [],
  },
  {
    id: 8,
    title: "Workplace Technology Skills",
    tagline: "Today's childcare professionals use technology daily.",
    intro: "Build comfort with the basics.",
    recommended: [
      "Check email regularly",
      "Use scheduling applications",
      "Complete online training",
      "Use digital attendance systems",
      "Follow center communication platforms",
      "Understand basic computer skills",
    ],
    avoid: [],
  },
  {
    id: 9,
    title: "Building Your Professional Resume",
    tagline: "Your resume opens the door.",
    intro: "Include these sections: Contact Info, Professional Summary, Education, CDA Coursework, Certifications, Work Experience, Volunteer Experience, Skills, and References.",
    recommended: [
      "Use a clean, professional template",
      "Highlight your CDA coursework and certifications",
      "Add volunteer experience with children",
      "Keep it to 1–2 pages",
    ],
    avoid: [],
  },
  {
    id: 10,
    title: "Interview Success",
    tagline: "Preparation builds confidence.",
    intro: "Before the interview: research the center, arrive 10–15 minutes early, dress professionally, and bring copies of your resume and certifications.",
    recommended: [
      "Practice common questions: Why do you want to work with children?",
      "Know your strengths",
      "Be ready to explain how you handle challenging behaviors",
      "Be able to describe what professionalism means to you",
    ],
    avoid: [],
  },
  {
    id: 11,
    title: "Career Growth Pathways",
    tagline: "A CDA is only the beginning.",
    intro: "Where your CDA can take you:",
    recommended: [
      "Assistant Teacher → Lead Teacher",
      "Program Director → Childcare Center Director",
      "Early Childhood Specialist",
      "Workforce Development Professional",
      "Higher education opportunities",
      "Entrepreneurship & childcare ownership",
    ],
    avoid: [],
  },
];

export const WORKFORCE_FINAL_MESSAGE =
  "A CDA credential opens doors, but professionalism keeps them open. Employers want candidates who are reliable, respectful, professional, and committed to serving children and families. Success in childcare is built on knowledge, professionalism, and consistency.";

// Shareable Canva resume templates
export const RESUME_TEMPLATES = [
  { name: "Functional Resume", note: "Best if you're new to the field — leads with skills", url: "https://canva.link/99yghfajwoei5p0" },
  { name: "Chronological Resume", note: "Best if you have steady work history", url: "https://canva.link/rwa2xnq2u3m4lgr" },
  { name: "Combination Resume", note: "Blends skills and work history", url: "https://canva.link/bnr3qd1ike1u3y8" },
];

// Professional Readiness Score — 20 questions. Best answer = 5 pts (others = 0). Max 100.
export const READINESS_QUIZ = [
  { q: "For an interview at a childcare center, the best footwear is:", options: ["Stilettos", "Comfortable closed-toe shoes", "Flip-flops"], answer: 1 },
  { q: "Which outfit is most appropriate for working with children?", options: ["Medical scrubs or clothing that allows movement", "A tight, revealing outfit", "A graphic tee with offensive text"], answer: 0 },
  { q: "Good daily hygiene for a childcare professional includes:", options: ["Skipping showers when busy", "Arriving showered, clean, and using deodorant", "Heavy perfume instead of bathing"], answer: 1 },
  { q: "If you are running late to your shift, you should:", options: ["Say nothing and hope no one notices", "Call immediately, notify your supervisor, and give an ETA", "Just show up whenever you arrive"], answer: 1 },
  { q: "A 'no call, no show' is:", options: ["Acceptable occasionally", "Never acceptable", "Fine if you're tired"], answer: 1 },
  { q: "When a supervisor gives you feedback, you should:", options: ["Argue and defend yourself", "Accept it professionally and learn from it", "Ignore it"], answer: 1 },
  { q: "Which is professional communication?", options: ["Gossiping about coworkers", "Listening actively and speaking respectfully", "Texting friends during class time"], answer: 1 },
  { q: "Posting negative comments about your job on social media is:", options: ["A good way to vent", "Unprofessional and can cost you your job", "Required"], answer: 1 },
  { q: "Protecting a child's and family's information is:", options: ["Optional", "A professional and legal responsibility", "Only the director's job"], answer: 1 },
  { q: "Maintaining professional boundaries means:", options: ["Oversharing your personal life with families", "Keeping workplace conversations professional and confidential", "Adding every parent on social media"], answer: 1 },
  { q: "When families drop off their child, you should:", options: ["Ignore them if you're busy", "Smile, greet them warmly, and be welcoming", "Look at your phone"], answer: 1 },
  { q: "Being a strong team member means:", options: ["Only doing your own tasks", "Helping coworkers, sharing responsibilities, and being flexible", "Skipping staff meetings"], answer: 1 },
  { q: "Which technology skill is expected in childcare today?", options: ["None — it's all paper", "Checking email and using digital attendance systems", "Avoiding computers entirely"], answer: 1 },
  { q: "Arguing with a coworker in front of children is:", options: ["Fine if you're right", "Unprofessional — handle it privately", "Encouraged"], answer: 1 },
  { q: "A strong childcare resume should include:", options: ["Only your name", "CDA coursework, certifications, experience, and references", "Just a phone number"], answer: 1 },
  { q: "Before an interview, you should arrive:", options: ["Right on time or a little late", "10–15 minutes early", "Whenever is convenient"], answer: 1 },
  { q: "A common interview question is:", options: ["What's your favorite TV show?", "Why do you want to work with children?", "How much vacation do I get?"], answer: 1 },
  { q: "Reliability and dependability are:", options: ["Not that important", "Among the most valued skills employers look for", "Only for managers"], answer: 1 },
  { q: "A CDA credential is:", options: ["The end of your career growth", "The beginning of many career pathways", "Only useful for one job"], answer: 1 },
  { q: "Overall, professionalism in childcare means being:", options: ["Late and unprepared", "Reliable, respectful, and consistent", "Focused only on yourself"], answer: 1 },
];

export const READINESS_BANDS = [
  { id: "ready", min: 90, label: "Workforce Ready", emoji: "🟢", color: "#16a34a", note: "You're employer-ready! Keep these habits as you start your career." },
  { id: "prep", min: 70, label: "Needs Additional Preparation", emoji: "🟡", color: "#ca8a04", note: "You're close — review the modules where you missed points, then retake." },
  { id: "coaching", min: 0, label: "Coaching Recommended", emoji: "🔴", color: "#dc2626", note: "Let's build these skills together. Ask Jazmin to coach you through the modules." },
];

export function scoreToBand(score) {
  return READINESS_BANDS.find((b) => score >= b.min) || READINESS_BANDS[READINESS_BANDS.length - 1];
}

// Founder
export const FOUNDER = {
  name: "Dr. Gabriela Ibarra Noriega",
  title: "Founder, Care Comply",
  initials: "GIN",
  bio: "Care Comply was founded by Dr. Gabriela Ibarra Noriega — a workforce development professional, educator, and advocate for educational access. Born in Monterrey, Mexico and raised in San Antonio, Texas, she's a first-generation college graduate (St. Mary's University; M.A. in Nonprofit Management; Ph.D. in Leadership Studies, Our Lady of the Lake University). She began her career as a young mother caring for infants in a West Side San Antonio childcare center, where she saw talented people held back not by a lack of passion — but a lack of access. Care Comply is her answer.",
  quote: "I believe every community already has future educators waiting for an opportunity. Too often, the barrier is not talent, passion, or commitment — it's access. Care Comply was built to remove those barriers and create pathways for individuals who want to serve children, strengthen families, and build meaningful careers.",
};
